import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, AlertCircle, CheckCircle, Truck } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import RMAStepper from '@/components/RMAStepper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { createRMA, getPublicSettings } from '@/lib/api';

// Labels for return reasons
const REASON_LABELS = {
  too_small: 'Troppo stretto/a',
  too_large: 'Troppo largo/a',
  different_fit: 'Vestibilità diversa',
  different_color: 'Colore diverso',
  defect: 'Difetto prodotto',
  changed_mind: 'Ho cambiato idea',
  other: 'Altro motivo'
};

// Helper function to check if item is bridal/wedding dress (respects overrides)
const isBridalItem = (item, overrides = []) => {
  if (overrides && overrides.length > 0) {
    const itemProductId = String(item.product_id || '');
    const override = overrides.find(o => String(o.product_id) === itemProductId);
    if (override) return override.force_category === 'bridal';
  }
  const title = (item.title || '').toLowerCase();
  const productType = (item.product_type || '').toLowerCase();
  const tags = (item.tags || '').toLowerCase();
  const bridalKeywords = ['abito da sposa', 'collezione sposa', 'abiti sposa', 'bride', 'bridal', 'wedding dress'];
  return bridalKeywords.some(keyword => 
    title.includes(keyword) || productType.includes(keyword) || tags.includes(keyword)
  );
};

// Return cost constants (fallback only, actual value comes from backend settings)
const RETURN_COST_BRIDAL_FALLBACK = 100.00;

const RMAStep3 = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [surveyData, setSurveyData] = useState({});
  const [chosenOffer, setChosenOffer] = useState('refund');
  const [iban, setIban] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmCondition, setConfirmCondition] = useState(false);
  const [confirmPackaging, setConfirmPackaging] = useState(false);
  const [confirmLegalAct, setConfirmLegalAct] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const missingEmail = order && !order.email;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitGuardRef = useRef(false);
  const [publicSettings, setPublicSettings] = useState({});
  
  // Check if order was paid with cash on delivery (contrassegno)
  const [isCashOnDelivery, setIsCashOnDelivery] = useState(false);
  
  // Post-exchange return state
  const [voucherChoice, setVoucherChoice] = useState(null); // "keep" or "cancel"

  useEffect(() => {
    const storedOrder = sessionStorage.getItem('currentOrder');
    const storedItems = sessionStorage.getItem('selectedItems');
    const storedSurvey = sessionStorage.getItem('rmaSurveyData');
    const storedOffer = sessionStorage.getItem('rmaChosenOffer');
    
    if (!storedOrder || !storedItems || !storedSurvey) {
      navigate('/');
      return;
    }
    
    const parsedOrder = JSON.parse(storedOrder);
    setOrder(parsedOrder);
    setSelectedItems(JSON.parse(storedItems));
    setSurveyData(JSON.parse(storedSurvey));
    
    // Check if order was paid with cash on delivery (COD)
    const paymentMethod = parsedOrder.payment_gateway_names?.[0]?.toLowerCase() || '';
    const isCOD = paymentMethod.includes('contrassegno') || 
                  paymentMethod.includes('cod') || 
                  paymentMethod.includes('cash') ||
                  paymentMethod.includes('payment on delivery');
    setIsCashOnDelivery(isCOD);
    
    if (storedOffer) {
      setChosenOffer(storedOffer);
    }
    
    // Load public settings for dynamic steps
    getPublicSettings().then(data => setPublicSettings(data)).catch(() => {});
  }, [navigate]);

  // Check if any item is bridal
  const hasBridalItem = selectedItems.some(item => isBridalItem(item, publicSettings.product_category_overrides));
  
  // Post-exchange return detection (must be before totalValue calculation)
  const isPostExchange = order?.is_exchange_order && publicSettings.post_exchange_return_enabled;
  const hasExchangeVoucher = isPostExchange && order?.exchange_voucher_info?.gift_card_id;
  const originalTotal = isPostExchange ? parseFloat(order?.original_order_total || '0') : 0;
  
  // Calculate costs
  // For exchange orders, use original_order_total instead of summing discounted prices
  const rawTotalValue = selectedItems.reduce((sum, item) => sum + parseFloat(item.price), 0);
  const totalValue = isPostExchange && order?.original_order_total 
    ? parseFloat(order.original_order_total) 
    : rawTotalValue;
  const numItems = selectedItems.length;
  
  // Check if any item is Elite (price > €298, non-bridal)
  const ELITE_PRICE_THRESHOLD = 298;
  const hasEliteItem = !hasBridalItem && selectedItems.some(item => parseFloat(item.price) > ELITE_PRICE_THRESHOLD);
  
  // Return cost from API (stored in sessionStorage by RMAStep2), or fallback
  const storedReturnCost = parseFloat(sessionStorage.getItem('rmaReturnCost') || '0');
  const fallbackReturnCost = hasBridalItem ? RETURN_COST_BRIDAL_FALLBACK : (hasEliteItem ? 45 : 35) + Math.max(0, numItems - 1) * 15;
  const costWaiver = order?.cost_waiver || false;
  const returnCost = costWaiver ? 0 : (storedReturnCost > 0 ? storedReturnCost : fallbackReturnCost);
  
  let refundAmount;
  if (isPostExchange && hasExchangeVoucher && voucherChoice === 'cancel') {
    // Option B: Cancel voucher, refund from original order
    refundAmount = originalTotal - returnCost;
  } else if (isPostExchange && hasExchangeVoucher && voucherChoice === 'keep') {
    // Option A: Keep voucher, refund from exchange product price
    refundAmount = totalValue - returnCost;
  } else if (isPostExchange && !hasExchangeVoucher) {
    // Scenario 1 & 2: no voucher, refund from original or higher price
    refundAmount = Math.max(totalValue, originalTotal) - returnCost;
  } else {
    // Normal return
    refundAmount = totalValue - returnCost;
  }

  const canSubmit = () => {
    if (!confirmCondition || !confirmPackaging || !confirmLegalAct) return false;
    // Post-exchange with voucher: require choice
    if (isPostExchange && hasExchangeVoucher && !voucherChoice) return false;
    // IBAN required only for cash on delivery orders with refund amount > 0
    if (isCashOnDelivery && chosenOffer === 'refund' && refundAmount > 0 && !iban.trim()) return false;
    return true;
  };

  const handleSubmit = async () => {
    // Guard: prevent double submission
    if (submitGuardRef.current) return;
    submitGuardRef.current = true;

    if (!canSubmit()) {
      toast.error('Completa tutti i campi obbligatori');
      submitGuardRef.current = false;
      return;
    }
    
    const finalEmail = order.email || contactEmail.trim();
    if (!finalEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(finalEmail)) {
      toast.error('Inserisci un indirizzo email valido per ricevere le comunicazioni');
      submitGuardRef.current = false;
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Build RMA items with survey data using new field names
      const rmaItems = selectedItems.map((item, idx) => {
        const itemId = item.unique_id || idx;
        const data = surveyData[itemId] || {};
        return {
          product_id: item.id,
          variant_id: item.variant_id,
          title: item.title,
          variant_title: item.variant_title,
          price: item.price,
          image_url: item.image_url,
          q1_event_type: data.q1_event_type || '',
          q2_event_status: data.q2_event_status || '',
          q3_return_reason: data.q3_return_reason || '',
          q3a_defect_type: data.q3a_defect_type || null,
          q4_used_size_guide: data.q4_used_size_guide || null,
          q5_try_on_conditions: data.q5_try_on_conditions || [],
          q6_time_to_event: data.q6_time_to_event || null,
          q7_preferred_solution: data.q7_preferred_solution || '',
          q8_fit_detail: data.q8_fit_detail || []
        };
      });
      
      const actualReturnMethod = publicSettings.shipping_method_rma || 'pickup';
      const rmaPayload = {
        order_id: order.id,
        order_number: order.order_number,
        customer_email: finalEmail,
        customer_name: order.customer_name,
        customer_phone: order.shipping_address?.phone || null,
        shipping_address: order.shipping_address || null,
        items: rmaItems,
        chosen_offer: chosenOffer,
        return_method: actualReturnMethod,
        iban: iban.trim() || null,
        notes: notes.trim() || null,
        delivered_at: order.delivered_at || null,
        fulfilled_at: order.fulfilled_at || null,
        cost_waiver: order.cost_waiver || false
      };
      
      // Add post-exchange return data if applicable
      if (isPostExchange) {
        rmaPayload.is_post_exchange_return = true;
        rmaPayload.original_order_number = order.original_order_number;
        rmaPayload.original_order_total = originalTotal;
        rmaPayload.exchange_request_id = order.exchange_request_id;
        if (hasExchangeVoucher) {
          rmaPayload.voucher_choice = voucherChoice;
          rmaPayload.exchange_voucher_info = order.exchange_voucher_info;
        }
      }
      
      const result = await createRMA(rmaPayload);
      
      // Clear RMA session data
      sessionStorage.removeItem('rmaSurveyData');
      sessionStorage.removeItem('rmaDefectPhotos');
      sessionStorage.removeItem('rmaChosenOffer');
      sessionStorage.removeItem('rmaChosenOfferData');
      sessionStorage.removeItem('rmaHasDefect');
      sessionStorage.removeItem('selectedItems');
      sessionStorage.removeItem('requestType');
      
      // Navigate to confirmation
      navigate(`/rma/confirmation/${result.rma_id}`);
    } catch (error) {
      console.error('Error creating RMA:', error);
      toast.error('Errore durante la creazione del reso');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!order) return null;

  return (
    <div className="page-container bg-white">
      <Header showBack backTo={`/order/${orderNumber}/rma/step2`} />
      <RMAStepper currentStep={3} />
      
      <main className="flex-1 py-8 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-3xl font-normal tracking-tight mb-2">
              Conferma Reso
            </h1>
            <p className="text-muted-foreground font-light mb-8">
              Verifica i dettagli e conferma la tua richiesta
            </p>
          </motion.div>

          {/* Missing Email Warning */}
          {missingEmail && (
            <motion.div
              className="luxury-card p-6 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
            >
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  Inserisci la tua email per ricevere le comunicazioni
                </p>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="nome@email.com"
                  className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  data-testid="rma-missing-email-input"
                />
                <p className="text-xs text-amber-600 mt-1.5">
                  Riceverai qui la conferma e tutte le comunicazioni sulla tua richiesta.
                </p>
              </div>
            </motion.div>
          )}

          {/* Order Summary */}
          <motion.div
            className="luxury-card p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
              Riepilogo Articoli
            </p>
            <div className="space-y-4">
              {selectedItems.map((item, idx) => {
                const itemId = item.unique_id || idx;
                const data = surveyData[itemId] || {};
                return (
                  <div key={itemId} className="flex items-start gap-4 pb-4 border-b border-border last:border-0 last:pb-0">
                    <div className="w-14 h-18 bg-secondary overflow-hidden flex-shrink-0">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.variant_title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Motivo: {REASON_LABELS[data.q3_return_reason] || data.q3_return_reason}
                      </p>
                    </div>
                    {!isPostExchange && (
                      <p className="font-medium">€{parseFloat(item.price).toFixed(2)}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Post-Exchange Voucher Choice */}
          {isPostExchange && hasExchangeVoucher && (
            <motion.div
              className="luxury-card p-6 mb-6 border-violet-300 bg-violet-50"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.12 }}
            >
              <p className="text-xs font-bold tracking-[0.15em] uppercase mb-2 text-violet-700">
                Reso Post-Cambio — Voucher Rimborso
              </p>
              <p className="text-sm text-violet-700 mb-4">
                Per il cambio precedente ti è stato assegnato un voucher di <strong>€{parseFloat(order.exchange_voucher_info?.value || 0).toFixed(2)}</strong>. Come preferisci procedere?
              </p>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setVoucherChoice('keep')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${voucherChoice === 'keep' ? 'border-violet-500 bg-violet-100' : 'border-gray-200 bg-white hover:border-violet-300'}`}
                  data-testid="voucher-choice-keep"
                >
                  <p className="font-semibold text-sm text-gray-900">Opzione A — Mantieni il voucher</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Tieni il voucher di €{parseFloat(order.exchange_voucher_info?.value || 0).toFixed(2)} e ricevi il rimborso monetario di <strong>€{(totalValue - returnCost).toFixed(2)}</strong> (valore prodotto - costi gestione).
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setVoucherChoice('cancel')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${voucherChoice === 'cancel' ? 'border-violet-500 bg-violet-100' : 'border-gray-200 bg-white hover:border-violet-300'}`}
                  data-testid="voucher-choice-cancel"
                >
                  <p className="font-semibold text-sm text-gray-900">Opzione B — Annulla il voucher</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Il voucher verrà annullato e riceverai il rimborso completo di <strong>€{(originalTotal - returnCost).toFixed(2)}</strong> (importo ordine originale - costi gestione).
                  </p>
                </button>
              </div>
            </motion.div>
          )}

          {/* Cost Summary */}
          <motion.div
            className="luxury-card p-6 mb-6 border-amber-300 bg-amber-50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase mb-4 text-amber-700">
              Riepilogo Rimborso
            </p>
            <div className="space-y-2">
              {isPostExchange && (
                <div className="flex justify-between text-sm">
                  <span>Importo ordine</span>
                  <span>€{originalTotal.toFixed(2)}</span>
                </div>
              )}
              {!isPostExchange && (
                <div className="flex justify-between text-sm">
                  <span>Valore articoli</span>
                  <span>€{totalValue.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span>Costo reso {numItems > 1 && !hasBridalItem && `(${numItems} articoli)`}</span>
                <span>-€{returnCost.toFixed(2)}</span>
              </div>
              <div className="border-t border-amber-300 pt-2 mt-2 flex justify-between items-center">
                <span className="font-bold">Rimborso stimato</span>
                <span className="text-2xl font-bold text-amber-700">
                  €{refundAmount.toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Cost Breakdown - Togglable */}
            {publicSettings.show_return_cost_explanation !== false && (
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-xs font-medium text-amber-800 mb-2">
                {publicSettings.cost_explanations?.return_title || 'Perché il reso ha questo costo?'}
              </p>
              <ul className="text-xs text-amber-700 space-y-1">
                {(publicSettings.cost_explanations?.return_items || 'Spedizione di andata - circa 13€ compreso di assicurazione|Spedizione di ritiro del prodotto - circa 13€ per richiesta di ritiro a domicilio|Sanificazione e rigenerazione dell\'abito - circa 5€|Stiratura professionale - circa 3€|Re-imballaggio per rimessa in vendita - circa 1€').split('|').map((item, i) => (
                  <li key={`cost-${item.slice(0,20)}-${i}`}>• {item}</li>
                ))}
              </ul>
              <p className="text-xs text-amber-600 mt-3">
                {publicSettings.cost_explanations?.return_note || 'Il costo del reso riflette un servizio completo, sicuro e gestito senza pensieri. Le spedizioni sono dotate di servizio assicurativo per proteggere i nostri clienti da rischi come furti e smarrimenti. Ci assumiamo tutte le responsabilità. Passiamo a ritirare il prodotto a domicilio e controlliamo ogni fase fino alla consegna nei nostri magazzini.'}
              </p>
              {hasBridalItem && (
                <p className="text-xs text-amber-800 mt-3 font-medium">
                  {(publicSettings.cost_explanations?.bridal_warning || 'Gli abiti della collezione sposa hanno un costo fisso di €{bridal_cost} per via dei tessuti pregiati che richiedono trattamenti specializzati.').replace('{bridal_cost}', (publicSettings.bridal_return_cost || 100).toFixed(0))}
                </p>
              )}
              {hasEliteItem && (
                <p className="text-xs text-amber-800 mt-3 font-medium">
                  {publicSettings.cost_explanations?.elite_warning || 'I capi della Elite Collection (prodotti oltre €298) hanno un costo di gestione di €45 per via dei tessuti di maggiore importanza che richiedono trattamenti specializzati.'}
                </p>
              )}
            </div>
            )}
          </motion.div>

          {/* Come funziona — RMA (dynamic from admin settings) */}
          {(() => {
            const method = publicSettings.shipping_method_rma || 'pickup';
            const allSteps = publicSettings.come_funziona_steps || {};
            const steps = (allSteps[`rma_${method}`] || []).filter(s => s.title);
            if (!steps.length) return null;
            return (
              <motion.div
                className="luxury-card p-6 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
                  Come funziona il reso
                </p>
                <div className="space-y-4">
                  {steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{step.title}</p>
                        {step.desc && <p className="text-xs text-muted-foreground">{step.desc}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })()}

          {/* Return Method */}
          <motion.div
            className="luxury-card p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
              Modalità Restituzione
            </p>
            {(() => {
              const method = publicSettings.shipping_method_rma || 'pickup';
              const labels = publicSettings.delivery_method_labels || {};
              const methodLabel = labels[`${method}_label`] || 'Ritiro a Domicilio';
              const methodDesc = labels[`${method}_description`] || '';
              return (
                <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3 mb-4">
                  <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">{methodLabel}</p>
                    {methodDesc && <p className="text-sm text-blue-700">{methodDesc}</p>}
                  </div>
                </div>
              );
            })()}
            <div className="bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                <strong>Nota:</strong> L'approvazione della richiesta avverrà dopo la verifica del rispetto della finestra di 14 giorni dalla consegna e del controllo delle condizioni del prodotto.
              </p>
            </div>
          </motion.div>

          {/* IBAN (only for cash on delivery orders) */}
          {isCashOnDelivery && chosenOffer === 'refund' && refundAmount > 0 && (
            <motion.div
              className="luxury-card p-6 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                IBAN per Rimborso <span className="text-red-500">*</span>
              </label>
              <Input
                value={iban}
                onChange={(e) => setIban(e.target.value.toUpperCase())}
                placeholder="IT60X0542811101000000123456"
                className="rounded-none font-mono"
                data-testid="iban-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Poiché l'ordine è stato pagato in contrassegno, inserisci l'IBAN su cui ricevere il rimborso.
              </p>
            </motion.div>
          )}

          {/* Notes */}
          <motion.div
            className="luxury-card p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
              Note Aggiuntive (facoltativo)
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Aggiungi eventuali note o informazioni utili..."
              className="rounded-none min-h-[80px] resize-none"
            />
          </motion.div>

          {/* Confirmations */}
          <motion.div
            className="space-y-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <label className="flex items-start gap-3 cursor-pointer p-4 border border-border hover:border-primary/50 transition-colors">
              <Checkbox
                checked={confirmCondition}
                onCheckedChange={(checked) => {
                  setConfirmCondition(checked);
                  setConfirmPackaging(checked);
                }}
                className="mt-0.5"
                data-testid="confirm-condition"
              />
              <span className="text-sm">
                Confermo che il prodotto è nelle <strong>condizioni originali</strong>, non è stato utilizzato 
                (se non per prova), con etichette e accessori intatti, e che lo imballerò in modo <strong>sicuro e adeguato</strong> per la spedizione.
              </span>
            </label>
            
            <label className="flex items-start gap-3 cursor-pointer p-4 border border-border hover:border-primary/50 transition-colors">
              <Checkbox
                checked={confirmLegalAct}
                onCheckedChange={setConfirmLegalAct}
                className="mt-0.5"
                data-testid="confirm-legal-act"
              />
              <span className="text-sm">
                Dichiaro che quanto sopra indicato corrisponde a verità, <strong>accetto che il rimborso sia calcolato al netto dei costi di gestione sopra indicati</strong>, 
                e sono consapevole che la presente dichiarazione costituisce <strong>atto certificato e firmato</strong>, come già accettato in fase di ordine.
              </span>
            </label>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <Button 
              onClick={() => navigate(`/order/${orderNumber}/rma/step2`)}
              variant="outline"
              className="rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
            
            <Button 
              onClick={handleSubmit}
              disabled={!canSubmit() || isSubmitting}
              className="luxury-btn-primary inline-flex items-center gap-3"
              data-testid="confirm-rma-btn"
            >
              {isSubmitting ? 'Elaborazione...' : 'Conferma Reso'}
              <CheckCircle className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default RMAStep3;
